import { API, SequenceConfigScope } from "@swivl-mono/api-helper";
import { hasCapability } from "@swivl-mono/helpers";
import { FMSCapabilityKey, FMSObjectType, FMSType } from "@swivl-mono/types";
import { useCallback, useEffect, useMemo, useState } from "react";

import { extractErrorReason } from "../../../../Helpers/API/extractErrorReason";

export type CapabilityCoverage = "all" | "some" | "none";

export interface UseLocationFmsCapabilitiesInput {
  scope: SequenceConfigScope;
}

export interface UseLocationFmsCapabilitiesResult {
  loading: boolean;
  error: string | null;
  /** Distinct FMS types across the scope's locations. */
  fmsTypes: FMSType[];
  /** Every location in the scope, including those with no FMS integration. */
  locationCount: number;
  /** Locations in the scope whose FMS has the capability implemented. */
  supportedCount(key: FMSCapabilityKey, objectType: FMSObjectType): number;
  coverage(key: FMSCapabilityKey, objectType: FMSObjectType): CapabilityCoverage;
  /** `coverage === "all"` — the gate a location-scope control uses. */
  supports(key: FMSCapabilityKey, objectType: FMSObjectType): boolean;
  /** Human label of the scope's FMS when there is exactly one, else null. */
  singleFmsType: FMSType | null;
  /** True when the org has an active Mandrill App — the account sequence emails are sent from (decision 41). */
  emailSenderConfigured: boolean;
}

/**
 * Which FMS-dependent controls the Sequences tab may show (ENG-3216 decision 25). The API
 * only says WHICH FMS is behind each location; the decision itself is read from the
 * capability matrix here, with `hasCapability`, exactly as the engine reads it.
 */
export function useLocationFmsCapabilities({ scope }: UseLocationFmsCapabilitiesInput): UseLocationFmsCapabilitiesResult {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locationIds, setLocationIds] = useState<number[]>([]);
  const [fmsTypeByLocationId, setFmsTypeByLocationId] = useState<Record<number, FMSType>>({});
  const [emailSenderConfigured, setEmailSenderConfigured] = useState(false);

  const scopeKey = `${scope.orgId}/${scope.groupId ?? "_"}/${scope.locationId ?? "_"}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    API.SequenceConfig.getScopeFmsTypes(scope)
      .then((result) => {
        if (cancelled) return;
        setLocationIds(result.locationIds);
        setFmsTypeByLocationId(result.fmsTypeByLocationId);
        setEmailSenderConfigured(result.emailSenderConfigured);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(extractErrorReason(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // scopeKey captures every scope field the request depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  const fmsTypes = useMemo(() => [...new Set(Object.values(fmsTypeByLocationId))], [fmsTypeByLocationId]);

  const supportedCount = useCallback(
    (key: FMSCapabilityKey, objectType: FMSObjectType): number =>
      locationIds.filter((locationId) => {
        const fmsType = fmsTypeByLocationId[locationId];
        return fmsType !== undefined && hasCapability(fmsType, objectType, key);
      }).length,
    [locationIds, fmsTypeByLocationId]
  );

  const coverage = useCallback(
    (key: FMSCapabilityKey, objectType: FMSObjectType): CapabilityCoverage => {
      if (locationIds.length === 0) return "none";
      const supported = supportedCount(key, objectType);
      if (supported === 0) return "none";
      return supported === locationIds.length ? "all" : "some";
    },
    [locationIds.length, supportedCount]
  );

  const supports = useCallback((key: FMSCapabilityKey, objectType: FMSObjectType): boolean => coverage(key, objectType) === "all", [coverage]);

  return {
    loading,
    error,
    fmsTypes,
    locationCount: locationIds.length,
    supportedCount,
    coverage,
    supports,
    singleFmsType: fmsTypes.length === 1 ? fmsTypes[0] : null,
    emailSenderConfigured
  };
}
